import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {font, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {RequestInfo} from "@khan/shared-core";
import {getSignupUrl, preserveSearchParams, useLocation} from "@khan/urls";

export function DontHaveAnAccount() {
    return (
        <LabelMedium tag="p" style={styles.label}>
            {t`Don’t have a Khan Academy account?`}{" "}
            <SignUpLink>{t`Sign up`}</SignUpLink>
        </LabelMedium>
    );
}

export function NeedAnAccount({
    signupPath,
}: {
    signupPath?: string;
}): React.ReactNode {
    return (
        <LabelMedium tag="p" style={styles.label}>
            {t`Need a Khan Academy account?`}{" "}
            <SignUpLink href={signupPath}>{t`Sign up today`}</SignUpLink>
        </LabelMedium>
    );
}

// This component is meant to be used for the Writing Coach signup page.
export function DontHaveAnAccountRoleSplit() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const teacherHref = preserveSearchParams(
        "/writing-coach/signup/teacher",
        params,
    );
    const learnerHref = preserveSearchParams("/writing-coach/join", params);
    return (
        <View>
            <LabelMedium tag="p" style={styles.splitLabel}>
                {t`Don’t have a Khan Academy account?`}
            </LabelMedium>
            <View style={styles.splitSignup}>
                <LabelMedium>
                    <SignUpLink href={teacherHref}>
                        {t`Sign up as a teacher`}
                    </SignUpLink>
                </LabelMedium>
                <LabelMedium>
                    <SignUpLink href={learnerHref}>
                        {t`Join class as a learner`}
                    </SignUpLink>
                </LabelMedium>
            </View>
        </View>
    );
}

function SignUpLink({
    children,
    style,
    href,
}: {
    children: string;
    style?: CSSProperties;
    href?: string;
}): React.ReactNode {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    return (
        <Link
            href={href || preserveSearchParams(getSignupUrl(), params)}
            target={RequestInfo.isExtension ? "_blank" : undefined}
            style={[styles.link, style]}
        >
            {children}
        </Link>
    );
}

const styles = StyleSheet.create({
    link: {
        fontWeight: font.weight.bold,
    },
    label: {
        margin: `${spacing.medium_16}px 0`,
    },
    splitLabel: {
        display: "flex",
        flexDirection: "column",
        gap: spacing.xSmall_8,
    },
    splitSignup: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    },
});
